"use client";

import { useMemo } from "react";
import type { Event } from "@/types/events";
import type { Reservation } from "@/types/reservations";
import type { Aviso } from "@/types/avisos";
import { formatDateShort } from "@/lib/format";

const WEEKDAYS: Record<number, string> = {
  0: "DOM", 1: "SEG", 2: "TER", 3: "QUA", 4: "QUI", 5: "SEX", 6: "SÁB",
};

const MONTHS_UPPER: Record<number, string> = {
  0: "JANEIRO", 1: "FEVEREIRO", 2: "MARÇO", 3: "ABRIL", 4: "MAIO", 5: "JUNHO",
  6: "JULHO", 7: "AGOSTO", 8: "SETEMBRO", 9: "OUTUBRO", 10: "NOVEMBRO", 11: "DEZEMBRO",
};

function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

function formatTime(time: string | null): string {
  if (!time) return "";
  return time.substring(0, 5);
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

type CalendarEntry =
  | (Event & { _type: "event" })
  | (Reservation & { _type: "reservation" })
  | (Aviso & { _type: "aviso" });

interface CalendarCardProps {
  events: Event[];
  reservations: Reservation[];
  avisos?: Aviso[];
  loading: boolean;
  onEventClick?: (event: Event) => void;
  onReservationClick?: (reservation: Reservation) => void;
  onAvisoClick?: (aviso: Aviso) => void;
}

const EVENT_PIP = "bg-[#3ed97a]";
const RESERVATION_PIP = "bg-[#f5b731]";
const AVISO_PIP = "bg-[#ff6b2b]";
const EVENT_BADGE = "bg-[#3ed97a]/15 text-[#3ed97a]";
const RESERVATION_BADGE = "bg-[#f5b731]/15 text-[#f5b731]";
const AVISO_BADGE = "bg-[#ff6b2b]/15 text-[#ff6b2b]";

export function CalendarCard({
  events,
  reservations,
  avisos = [],
  loading,
  onEventClick,
  onReservationClick,
  onAvisoClick,
}: CalendarCardProps) {
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const days = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      result.push(date);
    }
    return result;
  }, [today]);

  const dateRange = useMemo(() => {
    const first = days[0];
    const last = days[days.length - 1];
    const y = first.getFullYear();
    if (first.getMonth() === last.getMonth()) {
      return `${MONTHS_UPPER[first.getMonth()]} ${y}`;
    }
    return `${MONTHS_UPPER[first.getMonth()]} / ${MONTHS_UPPER[last.getMonth()]} ${y}`;
  }, [days]);

  const itemsByDate = useMemo(() => {
    const map = new Map<string, { events: Event[]; reservations: Reservation[]; avisos: Aviso[] }>();
    const ensure = (key: string) => {
      if (!map.has(key)) map.set(key, { events: [], reservations: [], avisos: [] });
    };
    for (const e of events) {
      const key = String(e.date).substring(0, 10);
      ensure(key);
      map.get(key)!.events.push(e);
    }
    for (const r of reservations) {
      const key = String(r.date).substring(0, 10);
      ensure(key);
      map.get(key)!.reservations.push(r);
    }
    for (const a of avisos) {
      const key = String(a.data).substring(0, 10);
      ensure(key);
      map.get(key)!.avisos.push(a);
    }
    return map;
  }, [events, reservations, avisos]);

  const hasAnyItems = events.length > 0 || reservations.length > 0 || avisos.length > 0;

  const dailySections = useMemo(() => {
    return days
      .map((date) => {
        const data = itemsByDate.get(toDateKey(date));
        if (!data || (data.events.length === 0 && data.reservations.length === 0 && data.avisos.length === 0)) {
          return null;
        }
        const combined: CalendarEntry[] = [
          ...data.events.map((e) => ({ ...e, _type: "event" as const })),
          ...data.reservations.map((r) => ({ ...r, _type: "reservation" as const })),
          ...data.avisos.map((a) => ({ ...a, _type: "aviso" as const })),
        ].sort((a, b) => {
          const timeA = a._type === "event" ? a.start_time : a._type === "reservation" ? a.time : (a.horario ?? "");
          const timeB = b._type === "event" ? b.start_time : b._type === "reservation" ? b.time : (b.horario ?? "");
          return (timeA || "99:99").localeCompare(timeB || "99:99");
        });
        return { date, items: combined };
      })
      .filter((s): s is { date: Date; items: CalendarEntry[] } => Boolean(s));
  }, [days, itemsByDate]);

  if (loading || !hasAnyItems) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {/* Header — month label */}
      <div className="flex items-center justify-between border-b border-border px-3.5 pt-3 pb-2.5">
        <span className="text-11 font-bold uppercase tracking-[0.09em] text-muted-foreground">
          {dateRange}
        </span>
      </div>

      {/* Day strip */}
      <div className="flex gap-0.5 px-2.5 pt-2 pb-1">
        {days.map((date) => {
          const isToday = isSameDay(date, today);
          const dayData = itemsByDate.get(toDateKey(date));
          const hasEvents = !!(dayData && dayData.events.length > 0);
          const hasReservations = !!(dayData && dayData.reservations.length > 0);
          const hasAvisos = !!(dayData && dayData.avisos.length > 0);

          return (
            <div
              key={date.toISOString()}
              className={`flex flex-1 flex-col items-center gap-1.5 rounded-lg px-0.5 pt-1.5 pb-[7px] transition-colors ${
                isToday ? "bg-primary" : "hover:bg-secondary"
              }`}
            >
              <span
                className={`text-2xs font-medium uppercase tracking-[0.06em] ${
                  isToday ? "text-black/45" : "text-muted-foreground/70"
                }`}
              >
                {WEEKDAYS[date.getDay()]}
              </span>
              <span
                className={`font-mono text-15 leading-none ${
                  isToday ? "font-bold text-primary-foreground" : "font-medium text-muted-foreground"
                }`}
              >
                {date.getDate()}
              </span>
              <div className="flex h-[5px] items-center gap-[3px]">
                {hasEvents && (
                  <span
                    className={`size-[5px] rounded-full ${isToday ? "bg-primary-foreground/45" : "bg-[#3ed97a]"}`}
                  />
                )}
                {hasReservations && (
                  <span
                    className={`size-[5px] rounded-full ${isToday ? "bg-primary-foreground/45" : "bg-[#f5b731]"}`}
                  />
                )}
                {hasAvisos && (
                  <span
                    className={`size-[5px] rounded-full ${isToday ? "bg-primary-foreground/45" : "bg-[#ff6b2b]"}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Events list */}
      <div className="flex flex-col px-3.5 pb-3.5 pt-1.5">
        {dailySections.map(({ date, items }, groupIdx) => (
          <div
            key={date.toISOString()}
            className={`mt-2 ${groupIdx > 0 ? "border-t border-border" : ""}`}
          >
            <div className="px-0 pt-2.5 pb-2 text-11 font-bold uppercase tracking-[0.08em] text-muted-foreground/70">
              {isSameDay(date, today) ? "Hoje · " : ""}
              {formatDateShort(toDateKey(date))}
            </div>

            {items.map((item, idx) => {
              const isLast = idx === items.length - 1;
              let time: string;
              let title: string;
              let subtitle: string;
              let pip: string;
              let badgeClass: string;
              let badgeLabel: string;
              let handleClick: (() => void) | undefined;

              if (item._type === "aviso") {
                time = formatTime(item.horario);
                title = item.titulo;
                subtitle = item.urgente ? "Urgente" : "";
                pip = AVISO_PIP;
                badgeClass = AVISO_BADGE;
                badgeLabel = item.mensagem ? "ver detalhes" : "Aviso";
                handleClick = item.mensagem ? () => onAvisoClick?.(item) : undefined;
              } else if (item._type === "event") {
                time = formatTime(item.start_time);
                title = item.name;
                subtitle =
                  item.musical_styles?.map((s) => s.name).join(" · ") ||
                  item.attractions?.map((a) => a.attraction_name).join(" · ") ||
                  "";
                pip = EVENT_PIP;
                badgeClass = EVENT_BADGE;
                badgeLabel = "Atração";
                handleClick = () => onEventClick?.(item);
              } else {
                time = formatTime(item.time);
                title = item.name;
                subtitle = `${item.seats} ${item.seats === 1 ? "lugar" : "lugares"} · ${item.location === "calcada" ? "Calçada" : "Mesa interna"}`;
                pip = RESERVATION_PIP;
                badgeClass = RESERVATION_BADGE;
                badgeLabel = "Reserva";
                handleClick = () => onReservationClick?.(item);
              }

              return (
                <button
                  key={`${item._type}-${item.id}`}
                  type="button"
                  onClick={handleClick}
                  className={`flex w-full items-center gap-2.5 py-2.5 text-left transition-opacity hover:opacity-75 ${
                    !isLast ? "border-b border-border" : ""
                  }`}
                >
                  <span className="min-w-[34px] font-mono text-11 text-muted-foreground/70">
                    {time || "—"}
                  </span>
                  <span className={`w-[3px] self-stretch min-h-[18px] rounded-sm ${pip}`} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-13 font-semibold text-foreground">
                      {title}
                    </p>
                    {subtitle && (
                      <p className="mt-0.5 truncate text-11 text-muted-foreground/70">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 whitespace-nowrap rounded-full px-2 py-[3px] text-2xs font-bold ${badgeClass}`}
                  >
                    {badgeLabel}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
